// src/pages/settings/MetersTab.tsx — onglet PDL / PCE (liste, filtre client, recherche, consommations inline, création/édition/suppression)

import { useState, useCallback, useEffect } from "react";
import type { ConfirmModalProps } from "../../components/ConfirmModal";
import { BtnEdit, BtnDelete } from "../../components/IconButtons";
import type { ClientRecord, SiteRecord } from "../../api/settingsApi";
import {
  listPdl, upsertPdl, deletePdl as apiDeletePdl,
  listPce, upsertPce, deletePce as apiDeletePce,
  type PdlRecord, type PceRecord,
} from "../../api/metersApi";
import Modal from "./Modal";
import { PdlForm } from "./PdlForm";
import { PceForm } from "./PceForm";
import { MeterConsoModal } from "./MeterConsoModal";
import { EngieImportModal } from "./EngieImportModal";
import { ClientFilterChips } from "./ClientFilterChips";

type MeterSubTab = "pdl" | "pce";

export function MetersTab({ clients, sites, setModal, showToast, canEditSge, canEditEngieSource }:{
  clients: ClientRecord[]; sites: SiteRecord[];
  setModal: (m: ConfirmModalProps | null) => void;
  showToast: (msg: string) => void;
  canEditSge: boolean;
  canEditEngieSource: boolean;
}) {
  const [meterSubTab,    setMeterSubTab]    = useState<MeterSubTab>("pdl");
  const [pdls,           setPdls]           = useState<PdlRecord[]>([]);
  const [pdlsLoading,    setPdlsLoading]    = useState(false);
  const [pdlsError,      setPdlsError]      = useState<string|null>(null);
  const [pces,           setPces]           = useState<PceRecord[]>([]);
  const [pcesLoading,    setPcesLoading]    = useState(false);
  const [pcesError,      setPcesError]      = useState<string|null>(null);
  const [showPdlForm,    setShowPdlForm]    = useState(false);
  const [showPceForm,    setShowPceForm]    = useState(false);
  const [editPdl,        setEditPdl]        = useState<PdlRecord|undefined>();
  const [editPce,        setEditPce]        = useState<PceRecord|undefined>();
  const [filterClient,   setFilterClient]   = useState("");
  const [queryMeters,    setQueryMeters]    = useState("");
  const [openConso,      setOpenConso]      = useState<{ meter: PdlRecord | PceRecord; isPdl: boolean } | null>(null);
  const [refreshing,     setRefreshing]     = useState(false);
  const [showEngieImport, setShowEngieImport] = useState(false);

  const loadPdls = useCallback(async () => {
    setPdlsLoading(true); setPdlsError(null);
    try { setPdls(await listPdl()); }
    catch(e) { setPdlsError((e as Error).message); }
    finally { setPdlsLoading(false); }
  }, []);

  const loadPces = useCallback(async () => {
    setPcesLoading(true); setPcesError(null);
    try { setPces(await listPce()); }
    catch(e) { setPcesError((e as Error).message); }
    finally { setPcesLoading(false); }
  }, []);

  useEffect(() => { loadPdls(); loadPces(); }, [loadPdls, loadPces]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try { await Promise.all([loadPdls(), loadPces()]); } finally { setRefreshing(false); }
  };

  const handleSavePdl = async (pdl: PdlRecord) => {
    const saved = await upsertPdl(pdl);
    setPdls(prev => { const i=prev.findIndex(x=>x.id===saved.id); return i>=0?prev.map((x,j)=>j===i?saved:x):[...prev,saved]; });
    showToast("PDL enregistré ✓");
  };

  const handleDeletePdl = (pdl: PdlRecord) => {
    setModal({ title:"Supprimer ce PDL ?",
      message:`Le PDL "${pdl.id}" sera définitivement supprimé.`,
      confirmLabel:"Supprimer", danger:true, onCancel:()=>setModal(null),
      onConfirm: async()=>{
        await apiDeletePdl(pdl.id);
        setPdls(p=>p.filter(x=>x.id!==pdl.id));
        setModal(null); showToast("PDL supprimé");
      },
    });
  };

  const handleSavePce = async (pce: PceRecord) => {
    const saved = await upsertPce(pce);
    setPces(prev => { const i=prev.findIndex(x=>x.id===saved.id); return i>=0?prev.map((x,j)=>j===i?saved:x):[...prev,saved]; });
    showToast("PCE enregistré ✓");
  };

  const handleDeletePce = (pce: PceRecord) => {
    setModal({ title:"Supprimer ce PCE ?",
      message:`Le PCE "${pce.id}" sera définitivement supprimé.`,
      confirmLabel:"Supprimer", danger:true, onCancel:()=>setModal(null),
      onConfirm: async()=>{
        await apiDeletePce(pce.id);
        setPces(p=>p.filter(x=>x.id!==pce.id));
        setModal(null); showToast("PCE supprimé");
      },
    });
  };

  const handleSetMeterSubTab = (st: MeterSubTab) => {
    setMeterSubTab(st); setOpenConso(null); setQueryMeters(""); setFilterClient("");
  };

  const isPdl = meterSubTab === "pdl";
  const meters = isPdl ? pdls : pces;
  const metersLoading = isPdl ? pdlsLoading : pcesLoading;
  const metersError   = isPdl ? pdlsError   : pcesError;
  const qm = queryMeters.trim().toLowerCase();
  const filteredMeters = meters.filter(m => {
    const mSite = sites.find(s => s.id === m.siteId);
    if (filterClient && mSite?.clientId !== filterClient) return false;
    if (!qm) return true;
    return m.id.toLowerCase().includes(qm)
      || (m.codePostal ?? "").toLowerCase().includes(qm)
      || (m.label ?? "").toLowerCase().includes(qm)
      || (mSite?.name ?? "").toLowerCase().includes(qm)
      || (isPdl && ((m as PdlRecord).segment ?? "").toLowerCase().includes(qm));
  }).sort((a, b) => a.id.localeCompare(b.id));
  return (
    <div style={{height:"100%",display:"flex",flexDirection:"column"}}>
      {/* Sub-tab bar */}
      <div style={{flexShrink:0,paddingBottom:12}}>
        <div style={{display:"flex",gap:0,marginBottom:12,borderBottom:"1px solid #f0f0f0",alignItems:"center",justifyContent:"space-between"}}>
          <div style={{display:"flex"}}>
            {(["pdl","pce"] as MeterSubTab[]).map(st=>(
              <button key={st} onClick={()=>handleSetMeterSubTab(st)}
                style={{background:"none",border:"none",cursor:"pointer",padding:"8px 18px",
                  fontFamily:"var(--ff-title)",fontSize:13,fontWeight:700,
                  color:meterSubTab===st?"var(--text-primary)":"var(--text-muted)",
                  borderBottom:meterSubTab===st?"2px solid var(--green-dim)":"2px solid transparent"}}>
                {st==="pdl" ? `⚡ PDL (${pdls.length})` : `🔥 PCE (${pces.length})`}
              </button>
            ))}
          </div>
          <div className="flex gap-8" style={{marginBottom:4}}>
            <button onClick={handleRefresh} disabled={refreshing} className="btn btn-ghost btn-sm">↻ Rafraîchir</button>
            <button onClick={()=>setShowEngieImport(true)} className="btn btn-ghost btn-sm">📥 Importer un fichier Engie</button>
            <button
              onClick={()=>{ if(isPdl){setEditPdl(undefined);setShowPdlForm(true);}else{setEditPce(undefined);setShowPceForm(true);} }}
              className="btn btn-black btn-sm" style={{border:"1.5px solid transparent"}}>
              ＋ Ajouter {isPdl?"un PDL":"un PCE"}
            </button>
          </div>
        </div>
        <input
          type="text"
          placeholder={`Rechercher un ${isPdl?"PDL":"PCE"}…`}
          value={queryMeters} onChange={e=>{ setQueryMeters(e.target.value); setOpenConso(null); }}
          className="field" style={{width:"100%",fontSize:13}}
        />
        <ClientFilterChips clients={clients} active={filterClient} style={{marginTop:8}}
          onChange={id => { setFilterClient(id); setOpenConso(null); }}
          count={id => id === "" ? meters.length : meters.filter(m => sites.find(s=>s.id===m.siteId)?.clientId === id).length}/>
      </div>

      <div style={{flex:1,minHeight:0,overflowY:"auto",paddingBottom:24}}>
        {metersLoading
          ? <div style={{textAlign:"center",padding:32,color:"var(--text-faint)"}}>Chargement…</div>
          : metersError
            ? <div style={{color:"var(--red)",fontSize:13,marginBottom:12}}>⚠ {metersError}</div>
            : meters.length === 0
              ? <div className="card p-32" style={{textAlign:"center",marginBottom:16}}>
                  <div style={{fontSize:32,marginBottom:10}}>{isPdl?"⚡":"🔥"}</div>
                  <p style={{fontSize:13,color:"var(--text-muted)"}}>
                    Aucun {isPdl?"PDL":"PCE"} configuré.
                  </p>
                </div>
              : filteredMeters.length === 0
                ? <div className="card p-32" style={{textAlign:"center",color:"var(--text-faint)"}}>
                    <div style={{fontSize:28,marginBottom:10}}>🔎</div>
                    <p style={{fontSize:13}}>
                      {queryMeters
                        ? `Aucun résultat pour « ${queryMeters} »${filterClient ? " pour ce client" : ""}.`
                        : `Aucun ${isPdl?"PDL":"PCE"} pour ce client.`}
                    </p>
                    {filterClient && (
                      <button onClick={()=>setFilterClient("")} className="btn btn-ghost btn-sm" style={{marginTop:8}}>
                        Voir tous les clients
                      </button>
                    )}
                  </div>
                : <div className="flex-col gap-8">
                  {filteredMeters.map(m => {
                    const site = sites.find(s=>s.id===m.siteId);
                    return (
                      <div key={m.id} className="card" style={{overflow:"hidden"}}>
                        {/* Card row */}
                        <div className="flex gap-12" style={{alignItems:"center",padding:"12px 16px"}}>
                          <div style={{width:36,height:36,borderRadius:6,
                            background:isPdl?"#fffbe6":"#fff5f0",
                            display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,flexShrink:0}}>
                            {isPdl?"⚡":"🔥"}
                          </div>
                          <div className="flex-1" style={{minWidth:0}}>
                            <div style={{fontSize:13,fontWeight:700,color:"var(--text-primary)",fontFamily:"var(--ff-title)",display:"flex",alignItems:"center",gap:6}}>
                              {m.id}
                              {isPdl && (m as PdlRecord).source === "enedis" && !(m as PdlRecord).sge && (
                                <span title="PDL non accessible via SGE" style={{fontSize:14,lineHeight:1}}>🚫</span>
                              )}
                              {isPdl && (m as PdlRecord).source === "enedis" && (
                                <span title="Source : Enedis" style={{fontSize:10,fontWeight:700,color:"#2196f3",border:"1px solid #2196f3",borderRadius:10,padding:"1px 7px",lineHeight:1.4}}>
                                  Enedis
                                </span>
                              )}
                              {isPdl && (m as PdlRecord).source === "deepki" && (
                                <span title="Source : Deepki" style={{fontSize:10,fontWeight:700,color:"var(--green-dim)",border:"1px solid var(--green-dim)",borderRadius:10,padding:"1px 7px",lineHeight:1.4}}>
                                  Deepki
                                </span>
                              )}
                              {isPdl && (m as PdlRecord).source === "engie" && (
                                <span title="Source : Engie" style={{fontSize:10,fontWeight:700,color:"#8e44ad",border:"1px solid #8e44ad",borderRadius:10,padding:"1px 7px",lineHeight:1.4}}>
                                  Engie
                                </span>
                              )}
                              {!isPdl && (m as PceRecord).source === "grdf" && (
                                <span title="Source : GRDF" style={{fontSize:10,fontWeight:700,color:"#2196f3",border:"1px solid #2196f3",borderRadius:10,padding:"1px 7px",lineHeight:1.4}}>
                                  GRDF
                                </span>
                              )}
                              {!isPdl && (m as PceRecord).source === "engie" && (
                                <span title="Source : Engie" style={{fontSize:10,fontWeight:700,color:"#8e44ad",border:"1px solid #8e44ad",borderRadius:10,padding:"1px 7px",lineHeight:1.4}}>
                                  Engie
                                </span>
                              )}
                            </div>
                            <div style={{fontSize:11,color:"var(--text-muted)",display:"flex",gap:12,flexWrap:"wrap",marginTop:2}}>
                              {m.label && <span>Libellé : <strong>{m.label}</strong></span>}
                              {isPdl && (m as PdlRecord).segment && <span>Segment : <strong>{(m as PdlRecord).segment}</strong></span>}
                              {isPdl && (m as PdlRecord).puissanceSouscrite && <span>P. souscrite : <strong>{(m as PdlRecord).puissanceSouscrite}</strong></span>}
                              {m.codePostal && <span>CP : <strong>{m.codePostal}</strong></span>}
                              <span>Site : <strong>{site?.name ?? (m.siteId ? m.siteId : <em style={{color:"var(--text-faint)"}}>non rattaché</em>)}</strong></span>
                            </div>
                          </div>
                          <div className="flex gap-8">
                            <button
                              onClick={()=>setOpenConso({ meter: m, isPdl })}
                              title={isPdl?"Consommations journalières":"Consommations mensuelles"}
                              style={{background:"none",border:"1px solid #ddd",
                                borderRadius:6,cursor:"pointer",padding:"5px 8px",
                                color:"var(--text-muted)",
                                fontSize:13,lineHeight:1,flexShrink:0}}>
                              📊
                            </button>
                            {isPdl
                              ? <BtnEdit onClick={()=>{setEditPdl(m as PdlRecord);setShowPdlForm(true);}} title="Modifier"/>
                              : <BtnEdit onClick={()=>{setEditPce(m as PceRecord);setShowPceForm(true);}} title="Modifier"/>}
                            {isPdl
                              ? <BtnDelete onClick={()=>handleDeletePdl(m as PdlRecord)} title="Supprimer"/>
                              : <BtnDelete onClick={()=>handleDeletePce(m as PceRecord)} title="Supprimer"/>}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
        }
      </div>

      {showPdlForm && (
        <Modal title={editPdl?"Modifier le PDL":"Nouveau PDL"} onClose={()=>setShowPdlForm(false)}>
          <PdlForm initial={editPdl} sites={sites} clients={clients} existingPdls={pdls} setModal={setModal} onSave={handleSavePdl} onClose={()=>setShowPdlForm(false)} canEditSge={canEditSge} canEditEngieSource={canEditEngieSource}/>
        </Modal>
      )}
      {showPceForm && (
        <Modal title={editPce?"Modifier le PCE":"Nouveau PCE"} onClose={()=>setShowPceForm(false)}>
          <PceForm initial={editPce} sites={sites} clients={clients} existingPces={pces} setModal={setModal} onSave={handleSavePce} onClose={()=>setShowPceForm(false)} canEditEngieSource={canEditEngieSource}/>
        </Modal>
      )}
      {openConso && (
        <MeterConsoModal
          meter={openConso.meter} isPdl={openConso.isPdl}
          site={sites.find(s=>s.id===openConso.meter.siteId)}
          showToast={showToast}
          canPurgeDeepki={canEditSge}
          onClose={()=>setOpenConso(null)}
        />
      )}
      {showEngieImport && (
        <EngieImportModal
          sites={sites} clients={clients} pdls={pdls} pces={pces}
          canEditEngieSource={canEditEngieSource}
          showToast={showToast}
          onImported={()=>{ loadPdls(); loadPces(); }}
          onClose={()=>setShowEngieImport(false)}
        />
      )}
    </div>
  );
}
